import React from 'react';
import { DollarSign, TrendingUp, Users, CreditCard, Download, RefreshCw } from 'lucide-react';
import { PaginationControls } from './AdminTableUtils';
import { useAdminPayments } from './hooks/useAdminPayments';
import FailedRenewalsPanel from './admin/FailedRenewalsPanel';
import CancellationReasonsWidget from './admin/CancellationReasonsWidget';

const fmtCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount / 100);
const fmtDate = (d) => new Date(d).toLocaleString();
const statusBadge = (s) => s === 'completed' ? 'bg-green-900 text-green-200' : s === 'pending' ? 'bg-yellow-900 text-yellow-200' : 'bg-red-900 text-red-200';

const STAT_CARDS = [
  { key: 'total_revenue', label: 'Total Revenue', icon: DollarSign, extra: TrendingUp, gradient: 'from-green-500 to-emerald-600', fmt: true },
  { key: 'monthly_revenue', label: 'Monthly Revenue', icon: DollarSign, gradient: 'from-blue-500 to-blue-600', fmt: true },
  { key: 'active_subscribers', label: 'Active Subscribers', icon: Users, gradient: 'from-purple-500 to-purple-600' },
  { key: 'total_transactions', label: 'Total Transactions', icon: CreditCard, gradient: 'from-orange-500 to-orange-600' },
];

const exportCSV = (transactions) => {
  const csv = [['Date', 'User Email', 'User Name', 'Currency', 'Amount', 'Status', 'Stripe ID'], ...transactions.map(t => [fmtDate(t.created_at), t.user_email || '', t.user_name || '', (t.currency || 'USD').toUpperCase(), fmtCurrency(t.amount), t.payment_status || '', t.stripe_session_id || ''])].map(r => r.join(',')).join('\n');
  const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' })); a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`; a.click();
};

const RevenueGrid = ({ title, entries }) => (
  <div className="bg-gray-800 rounded-xl p-6 border border-gray-700" data-testid={`revenue-grid-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <h3 className="text-lg font-bold text-white mb-4">{title}</h3>
    {entries?.length ? (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {entries.map(([key, data]) => (<div key={key} className="bg-gray-900 rounded-lg p-4 border border-gray-700"><div className="text-sm text-gray-400 mb-1">{key}</div><div className="text-2xl font-bold text-white mb-1">{fmtCurrency(data.revenue)}</div><div className="text-xs text-gray-500">{data.count} transaction{data.count !== 1 ? 's' : ''}</div></div>))}
      </div>
    ) : (
      <div className="text-center py-8 text-gray-400 text-sm bg-gray-900/40 rounded-lg border border-dashed border-gray-700">
        No completed transactions yet — this view fills up once payments start clearing.
      </div>
    )}
  </div>
);

const TransactionRow = ({ t, i }) => (
  <tr key={`pay-${i}`} className="hover:bg-gray-750 transition">
    <td className="px-6 py-4 text-sm text-gray-300">{fmtDate(t.created_at)}</td>
    <td className="px-6 py-4"><div className="text-sm text-white font-medium">{t.user_name || 'Unknown'}</div><div className="text-xs text-gray-400">{t.user_email}</div></td>
    <td className="px-6 py-4"><span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-900 text-blue-200">{(t.currency || 'USD').toUpperCase()}</span></td>
    <td className="px-6 py-4 text-sm text-white font-semibold">{fmtCurrency(t.amount)}</td>
    <td className="px-6 py-4"><span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusBadge(t.payment_status)}`}>{t.payment_status || 'unknown'}</span></td>
    <td className="px-6 py-4 text-xs text-gray-400 font-mono">{t.stripe_session_id ? t.stripe_session_id.substring(0, 20) + '...' : 'N/A'}</td>
  </tr>
);

const TransactionsContent = ({ loading, paginatedTxns, totalCount, txnPage, setTxnPage }) => {
  if (loading) return <div className="text-center py-12 text-gray-400"><RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />Loading...</div>;
  if (totalCount === 0) return <div className="text-center py-12 text-gray-400"><CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No transactions found</p></div>;
  return (
    <>
      <table className="w-full">
        <thead className="bg-gray-900">
          <tr>{['Date','User','Currency','Amount','Status','Stripe ID'].map(h => <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {paginatedTxns.map((t, i) => <TransactionRow key={`pay-${i}`} t={t} i={i} />)}
        </tbody>
      </table>
      <PaginationControls currentPage={txnPage} totalItems={totalCount} onPageChange={setTxnPage} />
    </>
  );
};

const AdminPaymentsManagement = () => {
  const {
    transactions, revenueStats, loading,
    statusFilter, setStatusFilter,
    txnPage, setTxnPage,
    paginatedTxns, sortedCountry,
    fetchData,
  } = useAdminPayments();

  return (
    <div className="space-y-6">
      {revenueStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {STAT_CARDS.map(({ key, label, icon: Icon, extra: Extra, gradient, fmt }) => (
            <div key={key} className={`bg-linear-to-br ${gradient} rounded-xl p-3 sm:p-6 text-white`}>
              <div className="flex items-center justify-between mb-2"><Icon className="w-6 h-6 sm:w-8 sm:h-8 opacity-80" />{Extra && <Extra className="w-4 h-4 sm:w-5 sm:h-5" />}</div>
              <div className="text-lg sm:text-3xl font-bold mb-1">{fmt ? fmtCurrency(revenueStats[key]) : revenueStats[key]}</div>
              <div className="text-xs sm:text-sm opacity-90">{label}</div>
            </div>
          ))}
        </div>
      )}
      <CancellationReasonsWidget />
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-3 sm:p-6 border-b border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div><h3 className="text-lg sm:text-xl font-bold text-white">Payment Transactions</h3><p className="text-sm text-gray-400 mt-1">View and manage all payment transactions</p></div>
            <div className="flex items-center gap-3">
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setTxnPage(1); }} className="bg-gray-700 text-white rounded-lg px-4 py-2 text-sm border border-gray-600"><option value="all">All Status</option><option value="completed">Completed</option><option value="pending">Pending</option><option value="failed">Failed</option></select>
              <button onClick={fetchData} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition flex items-center space-x-2" disabled={loading}><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /><span className="hidden sm:inline">Refresh</span></button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <TransactionsContent loading={loading} paginatedTxns={paginatedTxns} totalCount={transactions.length} txnPage={txnPage} setTxnPage={setTxnPage} />
        </div>
        {transactions.length > 0 && (
          <div className="p-4 border-t border-gray-700 bg-gray-900 flex justify-between items-center">
            <span className="text-sm text-gray-400">{transactions.length} transaction{transactions.length !== 1 ? 's' : ''} total</span>
            <button onClick={() => exportCSV(transactions)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition flex items-center space-x-2 text-sm"><Download className="w-4 h-4" /><span>Export CSV</span></button>
          </div>
        )}
      </div>
      <RevenueGrid title="Revenue by Currency" entries={revenueStats?.revenue_by_currency ? Object.entries(revenueStats.revenue_by_currency) : []} />
      <RevenueGrid title="Revenue by Country" entries={sortedCountry} />
      <FailedRenewalsPanel />
    </div>
  );
};

export default AdminPaymentsManagement;
